import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { isProblemError } from '../../api/client';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  useCancelOccurrenceByStudent,
  useCancelOccurrenceByTeacher,
  useListOccurrences,
  useListScheduleCollisions,
} from '../../api/generated/scheduling/scheduling';
import type { ClassOccurrence, Me } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { ConfirmDialog, Dialog } from '../../design/primitives/Dialog';
import { Field } from '../../design/primitives/Field';
import { formatDateInPersonTimezone, formatTimeInPersonTimezone } from '../../i18n/datetime';
import { readTeacherCanCancel } from './occurrenceView';
import { startOfLocalDay } from './wallClock';
import styles from './schedule.module.css';

type ViewMode = 'day' | 'week' | 'month';

/** Quantos dias cada modo cobre a partir do inicio do dia corrente. */
const SPAN_DAYS: Record<ViewMode, number> = { day: 1, week: 7, month: 31 };

/** Aulas que tem sala para entrar. Fora destes dois estados nao ha sessao viva. */
const LIVE_STATUS = new Set(['open', 'in_progress']);

/**
 * Rota autenticada do `.ics`, escrita a mao e nao gerada.
 *
 * `downloadMyCalendar` carrega `x-client-generation: manual` no contrato: o
 * corpo e `text/calendar`, e nao JSON, entao nao ha cliente TypeScript a gerar —
 * o cliente do navegador **e um link de download**. Um `fetch` avulso aqui seria
 * pior de duas formas: quebraria a regra de que toda chamada a plataforma vem do
 * cliente gerado e ainda precisaria remontar o `Content-Disposition` a mao.
 *
 * A URL e relativa e a sessao e o cookie `HttpOnly`, entao o download sai
 * autenticado sem que nenhum token seja tocado em JavaScript. **Nao existe rota
 * publica com token de assinatura para este arquivo**, e e de proposito: uma URL
 * permanente seria leitura sem sessao expondo a agenda de uma pessoa a quem
 * tivesse o link.
 */
export const MY_CALENDAR_URL = '/v1/me/calendar.ics';

/**
 * A janela comeca na MEIA-NOITE do dia corrente, no fuso da pessoa.
 *
 * Comecava em `new Date()`, com milissegundo: a chave da consulta nascia inedita
 * a cada montagem — `staleTime` nunca valia, toda visita refazia o GET e deixava
 * a entrada anterior orfa no cache — e, pior, as 14h a aba "Dia" ja nao mostrava
 * a aula das 8h. A aula EM ANDAMENTO, que comecou minutos atras, tambem ficava
 * de fora, e com ela sumia o unico caminho ate a sala.
 *
 * O fim soma dias corridos ao inicio: o limite superior de uma consulta nao
 * precisa cair na meia-noite civil do outro lado de uma virada de horario de
 * verao, e ancorar so o inicio ja e o que torna a chave estavel dentro do dia.
 */
function windowFor(mode: ViewMode, timezone: string): { from: string; to: string } {
  const from = startOfLocalDay(new Date(), timezone);
  const to = new Date(Date.parse(from) + SPAN_DAYS[mode] * 86400000).toISOString();
  return { from, to };
}

/**
 * O que dizer quando o cancelamento falha.
 *
 * As duas mutacoes nao tinham `onError` nenhum: em 409 — que e o desfecho
 * ESPERADO do professor dentro das 24 h e do aluno fora do prazo — o giro
 * apagava e a tela nao dizia nada. A pessoa clicava de novo e concluia que a
 * plataforma tinha travado. `schedule.cancelConflict` ja existia nos tres
 * catalogos, escrita e sem uso.
 */
function cancelMessage(error: unknown, t: (key: string) => string): string | null {
  if (error === null || error === undefined) return null;
  if (isProblemError(error) && error.status === 409) return t('schedule.cancelConflict');
  return t('error.unexpected');
}

function OccurrenceRow({
  me,
  occ,
  onChanged,
}: {
  readonly me: Me;
  readonly occ: ClassOccurrence;
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const studentCancel = useCancelOccurrenceByStudent();
  const teacherCancel = useCancelOccurrenceByTeacher();
  const [studentOpen, setStudentOpen] = useState(false);
  const [teacherOpen, setTeacherOpen] = useState(false);
  const [reason, setReason] = useState('');

  // Derivado da ocorrencia a cada renderizacao, e nao congelado num `useState`
  // inicializador: o valor e inteiramente funcao da prop e do relogio, e
  // congelado ele contradiz a lista logo depois do primeiro refetch.
  const teacherBlocked = !readTeacherCanCancel(occ);
  const isFreed = occ.status === 'cancelled_by_student';
  const canStudentCancel = me.role === 'student' && occ.status === 'scheduled';
  const canTeacherCancel = me.role === 'teacher' && occ.status === 'scheduled';
  const when = `${formatDateInPersonTimezone(occ.startsAtUtc, me.timezone, me.locale)} ${formatTimeInPersonTimezone(occ.startsAtUtc, me.timezone, me.locale)}`;
  const failure = cancelMessage(studentCancel.error, t) ?? cancelMessage(teacherCancel.error, t);

  return (
    <li
      className={[styles.item, isFreed ? styles.freed : ''].filter(Boolean).join(' ')}
      data-testid={`occ-${occ.id}`}
    >
      <p className={styles.when}>{when}</p>
      <p>
        {t(`schedule.status.${occ.status}`)} · {t(`schedule.kind.${occ.kind}`)}
      </p>
      {isFreed && me.role === 'teacher' ? (
        <p className={styles.freedLabel}>{t('schedule.freedSlot')}</p>
      ) : null}

      {/*
        A entrada na sala vivia num cartao que nenhuma tela importava, e por isso
        o caminho central do produto so era alcancavel digitando /room/<uuid> —
        um identificador que a interface nao mostra em lugar nenhum. Sem o
        professor admitido a aula nao vira `in_progress` e o item de folha nao
        nasce: o link ausente custava dinheiro, e nao so conforto.
      */}
      {LIVE_STATUS.has(occ.status) ? (
        <Link to={`/room/${occ.id}`}>{t('room.enterRoom')}</Link>
      ) : null}

      {failure !== null ? <Alert tone="warning">{failure}</Alert> : null}

      {canStudentCancel ? (
        <Button
          variant="secondary"
          loading={studentCancel.isPending}
          onClick={() => setStudentOpen(true)}
        >
          {t('schedule.cancelStudent')}
        </Button>
      ) : null}
      {canTeacherCancel ? (
        <>
          <Button variant="secondary" disabled={teacherBlocked} onClick={() => setTeacherOpen(true)}>
            {t('schedule.cancelTeacher')}
          </Button>
          {teacherBlocked ? <p className={styles.hint}>{t('schedule.teacherCancelBlocked')}</p> : null}
        </>
      ) : null}

      {/*
        Desmarcar era um clique unico numa lista de linhas iguais: a aula de
        amanha caia sem aviso e sem desfazer, com efeito sobre o direito a
        reposicao e sobre o item de folha do professor. A confirmacao nomeia
        **qual** aula vai cair — a data e a hora sao o que distingue uma linha da
        outra.
      */}
      <ConfirmDialog
        open={studentOpen}
        title={t('schedule.cancelStudent')}
        description={when}
        confirmLabel={t('schedule.cancelStudent')}
        loading={studentCancel.isPending}
        onConfirm={() =>
          studentCancel.mutate(
            { occurrenceId: occ.id },
            {
              onSuccess: onChanged,
              onSettled: () => setStudentOpen(false),
            },
          )
        }
        onCancel={() => setStudentOpen(false)}
      />

      {/*
        O dialogo do professor pedia "Justificativa" no corpo — rotulo de campo
        sem campo — e enviava a string '-' cravada. O motivo e insumo do alerta
        de professor faltoso e da decisao de pagar ou nao a aula; uma coluna de
        tracos e pior que a ausencia, porque parece preenchida. `ConfirmDialog`
        nao serve aqui: ele so aceita descricao em texto.
      */}
      <Dialog
        open={teacherOpen}
        title={t('schedule.cancelTeacherTitle')}
        onClose={() => setTeacherOpen(false)}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setTeacherOpen(false)}
              disabled={teacherCancel.isPending}
            >
              {t('action.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={teacherCancel.isPending}
              disabled={reason.trim().length === 0}
              onClick={() =>
                teacherCancel.mutate(
                  { occurrenceId: occ.id, data: { reason: reason.trim() } },
                  {
                    onSuccess: () => {
                      setReason('');
                      onChanged();
                    },
                    onSettled: () => setTeacherOpen(false),
                  },
                )
              }
            >
              {t('schedule.cancelTeacher')}
            </Button>
          </>
        }
      >
        <p>{when}</p>
        <Field
          label={t('schedule.cancelReason')}
          name="reason"
          required
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Dialog>
    </li>
  );
}

export function SchedulePage() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;
  const [mode, setMode] = useState<ViewMode>('week');
  // O fuso entra na dependencia: a janela e recortada no dia civil da pessoa, e
  // sem ele o `useMemo` guardaria a janela calculada no fuso errado da primeira
  // pintura. Nenhuma consulta sai antes de `me` existir (`enabled` abaixo).
  const timezone = me?.timezone;
  const range = useMemo(() => windowFor(mode, timezone ?? 'UTC'), [mode, timezone]);
  const occQuery = useListOccurrences(range, { query: { enabled: !!me } });
  const collisions = useListScheduleCollisions(undefined, {
    query: { enabled: me?.role === 'super_admin' },
  });

  if (!me) return null;
  const items = occQuery.data?.items ?? [];

  return (
    <div className={styles.page}>
      <h1>{t('schedule.title')}</h1>
      {/*
        Botoes de alternancia, e nao abas. Com `role="tab"` o leitor de tela
        anunciava "aba, 1 de 3" e prometia o padrao ARIA inteiro — setas movendo
        o foco, `tabindex` rotativo, um `tabpanel` associado por `aria-controls`
        —, e nenhuma das tres partes existia: as setas nao faziam nada e a lista
        de aulas nao tinha relacao anunciada com a aba escolhida. `aria-pressed`
        descreve o que estes controles realmente sao e ja funciona por teclado.
      */}
      <div className={styles.toolbar} role="group" aria-label={t('schedule.viewMode')}>
        {(['day', 'week', 'month'] as const).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={mode === m}
            className={mode === m ? styles.activeTab : styles.tab}
            onClick={() => setMode(m)}
          >
            {t(`schedule.mode.${m}`)}
          </button>
        ))}
      </div>
      {/*
        Fora do grupo: um botao de alternancia num link de download nao seria nem
        alternancia nem download, e leitor de tela leria a coisa errada.
      */}
      <p className={styles.export}>
        <a className={styles.exportLink} href={MY_CALENDAR_URL} download>
          {t('schedule.exportCalendar')}
        </a>
        <span className={styles.hint}>{t('schedule.exportCalendarHint')}</span>
      </p>
      {occQuery.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      {/*
        `!occQuery.isError` faz parte da guarda, e nao so `!isPending`. Com
        `retry: false` em `app/queryClient.ts`, a consulta que falhou deixa de
        estar pendente no mesmo instante: a tela mostrava o alerta de falha e,
        logo abaixo, "Nenhuma aula neste periodo" — duas afirmacoes que se
        contradizem, e a segunda e a que a pessoa acredita, porque e a unica que
        fala da agenda dela. E a mesma familia do vazio que a fila de colisao
        dizia por cima de um 500.
      */}
      {items.length === 0 && !occQuery.isPending && !occQuery.isError ? (
        <p>{t('schedule.empty')}</p>
      ) : null}
      <ul className={styles.list}>
        {items.map((occ) => (
          <OccurrenceRow key={occ.id} me={me} occ={occ} onChanged={() => void occQuery.refetch()} />
        ))}
      </ul>
      {me.role === 'super_admin' ? (
        <section aria-labelledby="collisions-title">
          <h2 id="collisions-title">{t('schedule.collisionsTitle')}</h2>
          <ul>
            {(collisions.data?.items ?? []).map((c) => (
              // Uma chave interpolada, e nao numero colado em substantivo: a
              // frase montada no JSX entregava ao tradutor "ocorrencias em
              // conflito" sem o numero, e com uma aula so saia "1 ocorrencias".
              // `schedule.changes.occurrenceCount` ja existe nos tres catalogos
              // e leva o `count` que o i18next usa para escolher a forma.
              <li key={c.id}>
                {c.teacher.displayName} ·{' '}
                {t('schedule.changes.occurrenceCount', { count: c.occurrenceIds.length })}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <p className={styles.timezoneNote}>{t('schedule.timezoneNote', { tz: me.timezone })}</p>
    </div>
  );
}
